import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import { Typography } from 'antd';
import { parseApiDate } from '../../utils/parseApiDate';

const { Text } = Typography;

export function usageMetricColor(value) {
  const percent = Number(value) || 0;
  if (percent >= 85) return 'var(--color-error)';
  if (percent >= 60) return 'var(--color-warning)';
  return 'var(--color-success)';
}

export function buildSystemMetrics(systemMetricsData) {
  if (!systemMetricsData) return [];

  const metrics = [
    { name: 'CPU usage', value: systemMetricsData.cpu_usage },
    { name: 'Memory usage', value: systemMetricsData.memory_usage },
    { name: 'Storage usage', value: systemMetricsData.storage_usage },
  ];

  if (systemMetricsData.gpu_metrics?.length) {
    systemMetricsData.gpu_metrics.forEach((gpu) => {
      metrics.push(
        { name: `GPU ${gpu.id} usage`, value: gpu.gpu_usage },
        { name: `GPU ${gpu.id} memory`, value: gpu.memory_usage },
      );
    });
  }

  return metrics.map((metric) => ({
    ...metric,
    color: usageMetricColor(metric.value),
  }));
}

export function formatPercentageChange(value, { invertColors = false } = {}) {
  if (value === null || value === undefined || value === 0) return null;

  const isUp = value >= 0;
  const isFavorable = invertColors ? !isUp : isUp;
  const Icon = isUp ? ArrowUpOutlined : ArrowDownOutlined;
  const color = isFavorable ? 'var(--color-success)' : 'var(--color-error)';

  return (
    <div className="dashboard-stat__change">
      <Icon style={{ color, marginRight: '4px' }} />
      <Text style={{ color }}>
        {isUp ? '+' : ''}{value.toFixed(1)}% from previous period
      </Text>
    </div>
  );
}

export function formatUptime(seconds) {
  if (!seconds) return 'N/A';

  const days = Math.floor(seconds / (24 * 3600));
  const hours = Math.floor((seconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts = [];
  if (days) parts.push(`${days} d`);
  if (hours) parts.push(`${hours} h`);
  if (minutes || (!days && !hours)) parts.push(`${minutes} m`);

  return parts.join(' ');
}

export function formatNumber(num) {
  if (num === null || num === undefined) return 'N/A';
  return new Intl.NumberFormat().format(num);
}

export function formatSince(isoString) {
  const date = new Date(isoString);
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatTimeAgo(isoString) {
  const now = new Date();
  const past = parseApiDate(isoString) || now;
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  return past.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: past.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export function computeErrorRate(requestsData) {
  if (!requestsData?.length) return null;
  let total = 0;
  let failed = 0;
  requestsData.forEach((item) => {
    const success = (item.api_successful || 0) + (item.direct_successful || 0)
      + (item.bot_successful || 0) + (item.widget_successful || 0)
      + (item.successful || 0);
    const fail = (item.api_failed || 0) + (item.direct_failed || 0)
      + (item.bot_failed || 0) + (item.widget_failed || 0)
      + (item.failed || 0);
    if (item.successful != null || item.failed != null) {
      total += success + fail;
      failed += fail;
    } else {
      total += success + fail;
      failed += fail;
    }
  });
  if (total === 0) return 0;
  return (failed / total) * 100;
}

export function mapActivityToDisplay(recentActivity) {
  return (recentActivity || []).map((activity) => {
    let icon;
    let statusClass;

    if (activity.status === 'success') {
      icon = <CheckCircleOutlined className="status-active" />;
      statusClass = 'success';
    } else if (activity.status === 'error') {
      icon = <AlertOutlined className="status-error" />;
      statusClass = 'error';
    } else if (activity.status === 'warning') {
      icon = <AlertOutlined className="status-warning" />;
      statusClass = 'warning';
    } else {
      icon = <AlertOutlined className="status-warning" />;
      statusClass = 'warning';
    }

    let title = activity.title;
    let userDisplay = null;
    let userClickable = null;

    if (activity.type === 'assistant_created' && activity.created_by) {
      userDisplay = activity.created_by;
      userClickable = activity.created_by_id;
      title = `${title} by `;
    } else if (activity.type === 'api_key_created' && activity.created_by) {
      userDisplay = activity.created_by;
      userClickable = activity.created_by_id;
      title = `${title} by `;
    } else if (activity.type === 'collection_created' && activity.created_by) {
      userDisplay = activity.created_by;
      userClickable = activity.created_by_id;
      title = `${title} by `;
    } else if (activity.type === 'file_uploaded' && activity.created_by) {
      userDisplay = activity.created_by;
      userClickable = activity.created_by_id;
      title = `${title} to '${activity.collection_name}' by `;
    } else if (activity.type === 'chat_error' && activity.error_details) {
      title = `${title}: ${activity.error_details}`;
    } else if (activity.type === 'api_error' && activity.error_details) {
      title = `${title}: ${activity.error_details}`;
    } else if (activity.type === 'bot_created' && activity.created_by) {
      userDisplay = activity.created_by;
      userClickable = activity.created_by_id;
      title = `${title} by `;
    } else if (activity.type === 'bot_error' && activity.error_details) {
      title = `${title}: ${activity.error_details}`;
    }

    const exactTime = (parseApiDate(activity.timestamp) || new Date()).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });

    return {
      title,
      userDisplay,
      userClickable,
      time: formatTimeAgo(activity.timestamp),
      exactTime,
      timestamp: activity.timestamp,
      status: statusClass,
      icon,
      botId: activity.bot_id || null,
      type: activity.type,
    };
  });
}
